import { z } from 'zod'
import {
  TILE_CONTENT,
  TILE_SIZES,
  MATERIAL_TYPES,
  FRAME_COLORS,
  MATTING,
  FILTERS,
  MAT_COLORS,
  COLLAGE_LAYOUT_STYLES,
  type MatColorKey,
} from '@mixtiles/web-backend-shared'
import { normalizeMatColorToKey } from 'utils/matColorUtils'
import { CROP_TYPE } from 'utils/cropUtils.consts'
import { CropAlignmentSchema } from 'utils/cropAlignment.consts'
import { CENTERPIECE_IMAGE_DIMENSIONS } from 'components/CenterpieceSelection/Centerpieces.const'
import { AiTemplateTypeSchema } from 'types/ai/template-schema'
import { PhotoFaceDataSchema } from 'components/PhotoBank/PhotoBank.schemas'

const NullableNumberSchema = z.preprocess(
  (val) => (val === null || val === undefined || Number.isNaN(val) ? 0 : val),
  z.number()
)
const ZoomSchema = z.preprocess(
  (val) => (val === null || val === undefined || Number.isNaN(val) ? 1 : val),
  z.number()
)
const CropSchema = z.object({
  x: NullableNumberSchema,
  y: NullableNumberSchema,
  width: NullableNumberSchema,
  height: NullableNumberSchema,
  zoom: ZoomSchema,
  cropType: z.nativeEnum(CROP_TYPE).optional(),
  wasSmartCropped: z.boolean().optional(),
})

export const MattingColorSchema = z
  .union([z.nativeEnum(MAT_COLORS), z.string()])
  .transform((val): MatColorKey => normalizeMatColorToKey(String(val)))

export const FrameColorSchema = z.nativeEnum(FRAME_COLORS)
export const MattingSchema = z.nativeEnum(MATTING)
export const FilterSchema = z.nativeEnum(FILTERS)

export const CustomStyleSchema = z.object({
  frameColor: FrameColorSchema.default(FRAME_COLORS.BLACK),
  matting: MattingSchema.default(MATTING.SHALLOW),
  filter: FilterSchema.default(FILTERS.NONE),
  mattingColor: MattingColorSchema.default(MAT_COLORS.WHITE),
  lastFramedColor: FrameColorSchema.default(FRAME_COLORS.BLACK),
})

const ArtistSchema = z
  .object({
    _id: z.string().default(''),
    uid: z.string().default(''),
    name: z.string().default(''),
    bio: z.string().default(''),
    profilePicture: z.string().default(''),
    coverPhoto: z.string().default(''),
    location: z.string().default(''),
    socialLinks: z.record(z.string()).default({}),
  })
  .passthrough()

const ClassificationSchema = z
  .object({
    name: z.string(),
    type: z.enum([
      'Category',
      'Color Scheme',
      'Medium',
      'Mood',
      'Room',
      'Style',
      'Variant',
    ]),
  })
  .passthrough()

const ArtworkSchema = z
  .object({
    id: z.string(),
    uid: z.string().default(''),
    title: z.string(),
    classifications: z.array(ClassificationSchema).default([]),
    collections: z.array(z.string()).default([]),
    keywords: z.array(z.string()).default([]),
    artworkSetNames: z.array(z.string()).optional(),
    colors: z.array(z.string()).default([]),
    artist: ArtistSchema,
    height: z.number(),
    width: z.number(),
    cloudinaryId: z.string(),
    cloudinaryName: z.string(),
    publicId: z.string(),
    orientation: z.enum(['portrait', 'square', 'landscape']),
    colorsHistogram: z.array(z.tuple([z.string(), z.number()])),
    palette: z.array(z.string()),
  })
  .passthrough()

const AiTemplateSchema = z
  .object({
    templateId: z.string().optional(),
    templateName: z.string().optional(),
    templateIndex: z.number().optional(),
    templateType: AiTemplateTypeSchema.optional(),
    templateCategory: z.string().optional(),
    userText: z.string().optional(),
    jobId: z.string().optional(),
    totalDurationSeconds: z.number().optional(),
    resultIndex: z.number().optional(),
    generationVariantIndex: z.number().optional(),
    variant: z.string().optional(),
    isVariation: z.boolean().optional(),
  })
  .passthrough()

export const AiTileGenerationStatusSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('pending'),
  }),
  z.object({
    status: z.literal('generating'),
    jobId: z.string(),
    startedAt: z.number(),
  }),
])

const CollageLayoutSchema = z.object({
  _id: z.string(),
  uid: z.string(),
  style: z.nativeEnum(COLLAGE_LAYOUT_STYLES),
  tileSize: z.nativeEnum(TILE_SIZES),
  photosCount: z.number(),
  svgPath: z.string(),
  textSvgPath: z.string().optional(),
  portfolioVariant: z.string().optional(),
})

const CollageTextSchema = z.object({
  showText: z.boolean(),
  text: z.string(),
  textStyleId: z.string().nullable(),
})

const CollagePhotoPositionSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
  transform: z.string().optional(),
})

const CropperDataSchema = z
  .object({
    crop: z.object({ x: z.number(), y: z.number() }).optional(),
    zoom: z.number().optional(),
  })
  .passthrough()

const ForgivingCropParamsSchema = z.preprocess((val) => {
  // Remove any own properties whose keys match fields from CropSchema
  const cropFields = Object.keys(CropSchema.shape)

  if (typeof val !== 'object' || val === null) {
    return val
  }

  // Clone to avoid mutating input in Zod
  const clean: Record<string, unknown> = { ...val } as Record<string, unknown>
  for (const key of cropFields) {
    if (Object.prototype.hasOwnProperty.call(clean, key)) {
      delete clean[key]
    }
  }
  return clean
}, z.record(CropSchema).default({}))

const CollagePhotoSlotDataSchema = z
  .object({
    uid: z.string().optional(),
    position: CollagePhotoPositionSchema.optional(),
    cropParams: ForgivingCropParamsSchema,
    cropperData: CropperDataSchema.default({}),
  })
  .passthrough()

const CustomCenterpiecePropsSchema = z.object({
  topText: z.string().optional(),
  bottomText: z.string().optional(),
})

export const baseTileFields = {
  id: z.string(),
  uploadedAt: z.number(),
  tileSize: z.nativeEnum(TILE_SIZES).default(TILE_SIZES.SQUARE_8X8),
  materialType: z.nativeEnum(MATERIAL_TYPES).default(MATERIAL_TYPES.CLASSIC),
  cropParams: ForgivingCropParamsSchema,
  cropperData: CropperDataSchema.default({}),
  customStyle: CustomStyleSchema.optional().nullable(),
  filterImageUrl: z.string().nullable().optional(),
  ignoreWarnings: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
  originalUrl: z.string().optional(),
}

const singlePhotoTileFields = {
  uid: z.string().optional(),
  width: z.number().default(0),
  height: z.number().default(0),
  useLowQualityAnyway: z.boolean().optional(),
  initialCropAlignment: CropAlignmentSchema.optional(),
}

export const PhotoTileDataSchema = z
  .object({
    ...baseTileFields,
    ...singlePhotoTileFields,
    tileContent: z.literal(TILE_CONTENT.PHOTO),
  })
  .passthrough()

export const ArtPhotoTileDataSchema = z.object({
  ...baseTileFields,
  ...singlePhotoTileFields,
  tileContent: z.literal(TILE_CONTENT.ART_PHOTO),
  artwork: ArtworkSchema,
  // Catalog category the artwork was picked from (used for analytics at order time)
  addedFromCategory: z.string().optional(),
})

export const AIGenPhotoTileDataSchema = z
  .object({
    ...baseTileFields,
    ...singlePhotoTileFields,
    tileContent: z.literal(TILE_CONTENT.AI_GEN_PHOTO),
    aiTemplate: AiTemplateSchema.default({}),
    sources: z
      .array(
        z.object({ uid: z.string(), face: PhotoFaceDataSchema.optional() })
      )
      .optional(),
    generationStatus: AiTileGenerationStatusSchema.optional(),
  })
  .passthrough()

const CollageSchema = z.object({
  photos: z.record(CollagePhotoSlotDataSchema),
  layout: CollageLayoutSchema,
  collageText: CollageTextSchema.optional(),
})

export const CollageTileDataSchema = z
  .object({
    ...baseTileFields,
    tileContent: z.literal(TILE_CONTENT.COLLAGE),
    width: z.number().optional(),
    height: z.number().optional(),
    isProcessing: z.boolean().optional(),
    collage: CollageSchema.optional(),
    isCreating: z.boolean().optional(),
    layout: CollageLayoutSchema.optional(),
    collageText: CollageTextSchema.optional(),
    photos: z.record(CollagePhotoSlotDataSchema).optional(),
  })
  .passthrough()

export const CenterpieceTileDataSchema = z
  .object({
    ...baseTileFields,
    tileSize: z.nativeEnum(TILE_SIZES).default(TILE_SIZES.RECTANGLE_25X8),
    materialType: z
      .nativeEnum(MATERIAL_TYPES)
      .default(MATERIAL_TYPES.FRAMELESS),
    tileContent: z.literal(TILE_CONTENT.CENTERPIECE),
    width: z.number().default(CENTERPIECE_IMAGE_DIMENSIONS.width),
    height: z.number().default(CENTERPIECE_IMAGE_DIMENSIONS.height),
    centerpieceTitle: z.string().optional(),
    highResUrl: z.string().optional(),
    lowResUrl: z.string().optional(),
    uid: z.string().optional(),
    customCenterpieceProps: CustomCenterpiecePropsSchema.optional(),
    isCustomized: z.boolean().optional(),
    localImageDimensions: z
      .object({ width: z.number(), height: z.number() })
      .optional(),
    localUrl: z.string().optional(),
    thumbUrl: z.string().optional(),
  })
  .passthrough()

export type TileCrop = z.infer<typeof CropSchema>
export type PhotoTileData = z.infer<typeof PhotoTileDataSchema>
export type ArtPhotoTileData = z.infer<typeof ArtPhotoTileDataSchema>
export type AIGenPhotoTileData = z.infer<typeof AIGenPhotoTileDataSchema>
export type AiTileGenerationStatus = z.infer<
  typeof AiTileGenerationStatusSchema
>
export type CollageTileData = z.infer<typeof CollageTileDataSchema>
export type CenterpieceTileData = z.infer<typeof CenterpieceTileDataSchema>
export type CollagePhotoSlotData = z.infer<typeof CollagePhotoSlotDataSchema>

export type SinglePhotoBaseTileData =
  | PhotoTileData
  | ArtPhotoTileData
  | AIGenPhotoTileData

export const TileDataSchema = z.discriminatedUnion('tileContent', [
  PhotoTileDataSchema,
  ArtPhotoTileDataSchema,
  AIGenPhotoTileDataSchema,
  CollageTileDataSchema,
  CenterpieceTileDataSchema,
])

export type TileData = z.infer<typeof TileDataSchema>
